"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WesolowskyDeleteDrift = void 0;
var WesolowskyPolynomialApproximator_1 = require("./WesolowskyPolynomialApproximator");
/// <summary>
/// Фильтр для подавления дрейфа (drift) сигнала на основе блочной полиномиальной аппроксимации.
/// Использует метод скользящего окна (overlap-add) с 50% перекрытием и окно Ханна для избежания артефактов склейки.
/// </summary>
var WesolowskyDeleteDrift = /** @class */ (function () {
    /// <summary>
    /// Инициализирует новый экземпляр фильтра.
    /// </summary>
    /// <param name="step">Длина шага сдвига между блоками. Размер буфера автоматически вычисляется как 2 * step для 50% перекрытия.</param>
    /// <param name="degree">Степень полинома аппроксимации (по умолчанию 3).</param>
    /// <param name="maxIterations">Максимальное число итераций алгоритма аппроксимации (по умолчанию 100).</param>
    /// <param name="residualThreshold">Порог остаточной ошибки для преждевременного завершения (по умолчанию 0).</param>
    function WesolowskyDeleteDrift(step, degree, maxIterations, residualThreshold) {
        if (degree === void 0) { degree = 3; }
        if (maxIterations === void 0) { maxIterations = 100; }
        if (residualThreshold === void 0) { residualThreshold = 0; }
        if (degree < 0)
            throw new Error('ArgumentException: degree');
        if (step <= 0)
            throw new Error('ArgumentException: step');
        // Установка длины буфера: удвоенный шаг для обеспечения 50% перекрытия (overlap)
        this._bufferLength = step * 2;
        this._step = step;
        this._maxIterations = maxIterations;
        this._residualThreshold = residualThreshold;
        // Вычисление величины Expansion.
        // Эта величина необходима для корректного определения смещения
        // (physicalStart) при копировании данных из исходного массива в рабочий буфер.
        this._expansion = this._bufferLength - 1;
        // new Float64Array(n) is zero-initialized, no explicit .fill(0) needed
        this._gridX = new Float64Array(this._bufferLength);
        for (var i = 0; i < this._bufferLength; i++)
            this._gridX[i] = i;
        this._approximator = new WesolowskyPolynomialApproximator_1.WesolowskyPolynomialApproximator(this._bufferLength, degree);
        this._approxResult = new WesolowskyPolynomialApproximator_1.AlgorithmResult();
        this._approximator.setGrid(this._gridX);
        this._signalBuffer = new Float64Array(this._bufferLength);
        this._window = new Float64Array(this._bufferLength);
        this._evaluated = new Float64Array(this._bufferLength);
    }
    Object.defineProperty(WesolowskyDeleteDrift.prototype, "expansion", {
        get: function () {
            return this._expansion;
        },
        enumerable: false,
        configurable: true
    });
    /// <summary>
    /// Применяет фильтр удаления дрейфа (drift removal) к указанному участку сигнала.
    /// Метод использует блочную полиномиальную аппроксимацию со скользящим окном (overlap-add)
    /// и окном Ханна для сглаживания границ между блоками и предотвращения артефактов склейки.
    /// Результат записывается в массив <paramref name="dst"/>.
    /// </summary>
    /// <param name="startIndex">
    /// Начальный индекс (позиция) в исходном сигнале, с которого нужно начать удаления дрифта.
    /// </param>
    /// <param name="length">
    /// Количество сэмплов в исходном сигнале, которые будут обработаны и записаны в результат.
    /// </param>
    /// <param name="src">
    /// Исходный массив аудиосигнала (float[]), из которого читаются данные.
    /// </param>
    /// <param name="dst">
    /// Целевой массив (float[]), в который записывается финальный обработанный результат.
    /// Должен иметь размер не менее <paramref name="length"/>.
    /// </param>
    /// <param name="originalSignalLength">
    /// Общая длина исходного сигнала. Используется для валидации границ и корректной работы
    /// с краями сигнала при выравнивании блоков.
    /// </param>
    /// <exception cref="ArgumentNullException">
    /// Возникает, если <paramref name="src"/> или <paramref name="dst"/> равны null.
    /// </exception>
    /// <exception cref="ArgumentException">
    /// Возникает, если длина временного буфера <paramref name="buffer"/> не совпадает с длиной исходного сигнала <paramref name="src"/>.
    /// Также может возникать, если рассчитанный диапазон обработки выходит за границы массива <paramref name="src"/>.
    /// </exception>
    /// <exception cref="ArgumentOutOfRangeException">
    /// Возникает, если <paramref name="startIndex"/> отрицательный или если диапазон обработки
    /// (startIndex + length) выходит за пределы общей длины сигнала (<paramref name="originalSignalLength"/>).
    /// </exception>
    WesolowskyDeleteDrift.prototype.filter = function (startIndex, length, srcArg, dst, originalSignalLength) {
        if (!srcArg || !dst)
            throw new Error('ArgumentNullException');
        if (srcArg.length === 0 || length <= 0)
            return;
        if (startIndex > originalSignalLength - length || startIndex < 0)
            throw new Error('ArgumentOutOfRangeException: startIndex');
        var src = srcArg instanceof Float64Array ? srcArg : new Float64Array(srcArg);
        /// Временный буфер для накопления результатов аппроксимации (double[]).
        /// Должен иметь длину, равную длине исходного сигнала <paramref name="src"/> (src.Length).
        /// Используется для алгоритма overlap-add внутри метода.
        var buffer = new Float64Array(srcArg.length);
        // 1. Границы ПОЛЕЗНЫХ данных (logical координаты)
        var dataStart = Math.max(0, startIndex);
        var dataEnd = Math.min(originalSignalLength, startIndex + length);
        // 2. Выравнивание к блокам размера _step
        // Начинаем с начала блока, который содержит dataStart
        // C# int division (truncation toward zero) → Math.trunc
        var processStart = Math.max(0, (Math.trunc(dataStart / this._step) - 1) * this._step);
        // Заканчиваем концом блока, который содержит dataEnd - 1
        var processEnd = ((dataEnd - 1) / this._step + 2) * this._step;
        // Ограничиваем processEnd длиной сигнала
        processEnd = Math.min(processEnd, originalSignalLength);
        var processLength = processEnd - processStart;
        // 3. Смещение полезной части внутри блока
        var resultOffset = dataStart - processStart;
        // 4. Физические координаты в src
        // Здесь Expansion должен быть применен к началу блока, если он нужен для центрирования
        // Но обычно Expansion — это часть алгоритма, а не сдвиг массива.
        // Если Expansion — это фиксированный сдвиг (например, 499), то:
        var physicalStart = processStart + this.expansion - startIndex;
        // Обработка при малой длине (два блока и менее)
        if (processLength <= this._bufferLength) {
            var rightPad = this._bufferLength - processLength;
            // Array.Copy(src, physicalStart, _signalBuffer, 0, processLength) — Float32Array → Float64Array
            this._signalBuffer.set(src.subarray(physicalStart, physicalStart + processLength), 0);
            for (var i = 0; i < rightPad; i++) {
                var sourceIndex = void 0;
                if (i < processLength) {
                    // Берём из исходного сигнала (зеркально)
                    sourceIndex = processLength - 1 - i;
                }
                else {
                    sourceIndex = processLength + (processLength - 1 - (i - processLength));
                }
                this._signalBuffer[length + i] = this._signalBuffer[sourceIndex];
            }
            this._approxResult = this._approximator.approximate(this._signalBuffer, this._maxIterations, this._residualThreshold);
            WesolowskyDeleteDrift.evaluatePolynomial(this._approxResult.finalCoefficients, this._gridX, this._evaluated);
            for (var i = 0; i < processLength; i++) {
                buffer[physicalStart + i] = this._evaluated[i];
            }
        }
        else {
            for (var i = 0; i < this._bufferLength; i++) {
                this._window[i] = 0.5 * (1.0 - Math.cos(2.0 * Math.PI * i / this._bufferLength));
            }
            // Поблочная обработка
            var totalBlocks = Math.trunc((processLength + this._step - 1) / this._step);
            var processedSamples = 0;
            for (var k = 0; k < totalBlocks; k++) {
                var blockStartInSrc = physicalStart + k * this._step;
                var currentBlockLength = Math.min(this._bufferLength, processLength - processedSamples);
                // Копируем данные в буфер блока
                // Array.Copy(src, blockStartInSrc, _signalBuffer, 0, currentBlockLength) — Float32Array → Float64Array
                this._signalBuffer.set(src.subarray(blockStartInSrc, blockStartInSrc + currentBlockLength), 0);
                // Сдвиг
                if (currentBlockLength < this._bufferLength && currentBlockLength > 0) {
                    var shiftValue = this._bufferLength - currentBlockLength;
                    var rampEnd = Math.min(shiftValue + this._step, this._bufferLength);
                    // Обнуляем окно в зоне shiftValue
                    for (var i = 0; i < shiftValue; i++)
                        this._window[i] = 0;
                    // Восстанавливаем форму окна Ханна в активной зоне
                    for (var i = shiftValue; i < rampEnd; i++) {
                        var relativeIndex = i - shiftValue;
                        this._window[i] = 0.5 * (1.0 - Math.cos(2.0 * Math.PI * relativeIndex / this._bufferLength));
                    }
                    // Зона за активным шагом получает вес 1
                    for (var i = rampEnd; i < this._bufferLength; i++)
                        this._window[i] = 1;
                    blockStartInSrc -= shiftValue;
                    currentBlockLength = this._bufferLength;
                    // Array.Copy(src, blockStartInSrc, _signalBuffer, 0, currentBlockLength) — Float32Array → Float64Array
                    this._signalBuffer.set(src.subarray(blockStartInSrc, blockStartInSrc + currentBlockLength), 0);
                    processedSamples -= shiftValue;
                    totalBlocks--; // Компенсация сдвига в счетчике блоков
                }
                // Аппроксимация и вычисление значений полинома
                this._approxResult = this._approximator.approximate(this._signalBuffer, this._maxIterations, this._residualThreshold);
                WesolowskyDeleteDrift.evaluatePolynomial(this._approxResult.finalCoefficients, this._gridX, this._evaluated);
                processedSamples += this._step;
                // Накапливаем результат с учетом веса окна (overlap-add)
                for (var i = 0; i < this._bufferLength; i++) {
                    var globalIdx = blockStartInSrc + i;
                    if (globalIdx >= src.length)
                        break;
                    // Границы определяем по абсолютной позиции сигнала
                    var isStartBoundary = globalIdx - physicalStart < this._step;
                    if (isStartBoundary && k === 0) {
                        buffer[globalIdx] = this._evaluated[i];
                    }
                    else {
                        buffer[globalIdx] += this._evaluated[i] * this._window[i];
                    }
                }
            }
        }
        // Копирование обработанного участка в целевой массив
        for (var i = 0; i < length; i++) {
            // C# (float) cast — implicit on assignment to Float32Array
            //dst[i] = buffer[physicalStart + resultOffset + i];
            dst[i] = src[physicalStart + resultOffset + i] - buffer[physicalStart + resultOffset + i];
        }
    };
    /// <summary>
    /// Вычисляет значения полинома по заданным коэффициентам и узлам сетки (метод Горнера).
    /// </summary>
    /// <param name="coeffs"> Коэффициенты </param>
    /// <param name="xValues"> Значения по оси x </param>
    /// <param name="yValues"> Массив результатов </param>
    /// <exception cref="ArgumentException"> Исключение в случае неправильной передачи коэффициентов </exception>
    WesolowskyDeleteDrift.evaluatePolynomial = function (coeffs, xValues, yValues) {
        if (!coeffs || coeffs.length === 0)
            throw new Error('ArgumentException');
        for (var i = 0; i < xValues.length; i++) {
            var x = xValues[i];
            // Метод Горнера: P(x) = c0 + x*(c1 + x*(c2 + ...))
            var y = coeffs[coeffs.length - 1];
            for (var k = coeffs.length - 2; k >= 0; k--) {
                y = y * x + coeffs[k];
            }
            yValues[i] = y;
        }
    };
    return WesolowskyDeleteDrift;
}());
exports.WesolowskyDeleteDrift = WesolowskyDeleteDrift;
